import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";

import { BestTimeSlotApi, BestTimeSlotApiWithIndex } from "api/account-queue";

interface TimeSlotsMap {
  [key: number]: number[];
}

export function parseTimeSlots(
  slots: Array<{ day: number; timeSlots: number[] }>
): TimeSlotsMap {
  return slots.reduce(
    (schedules, item) => ({
      ...schedules,
      [item.day]: item.timeSlots,
    }),
    {}
  );
}

export function formatTimeSlots(
  timeSlotsMap: TimeSlotsMap
): Array<{ day: number; timeSlots: number[] }> {
  return Object.entries(timeSlotsMap).map(([key, timeSlots]) => ({
    day: Number.parseInt(key),
    timeSlots,
  }));
}

export function getSuggestedTime(slots: number[]) {
  if (!slots?.length || slots.length === 0) {
    return 540;
  }
  return slots[slots.length - 1] + 15;
}

const weekDays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

interface TimeSlotsByDay {
  weekDay: string;
  weekDayNumber: number;
  timeSlots: Array<
    | { type: "timeSlot"; value: number; bestTimeIndex?: number }
    | {
        type: "bestTimeSuggestions";
        value: number;
        online: number;
        bestTimeIndex: number;
      }
  >;
}

export function getTimeSlotsByDays(
  schedules: TimeSlotsMap,
  bestTimeSlots: BestTimeSlotApi[],
  withBestTime: boolean
): TimeSlotsByDay[] {
  const bestTimeByDays = groupBy(
    bestTimeSlots.map((slot, index) => ({ ...slot, index: index + 1 })),
    (slot) => slot.day
  );

  function findBestTimeSlot(day: number, value: number) {
    if (!withBestTime) {
      return;
    }
    const slot = (bestTimeByDays[day] ?? []).find(
      (bestTime) => bestTime.offset === value
    );

    return slot;
  }

  function createTimeSlot(
    day: number,
    value: number
  ): { type: "timeSlot"; value: number; bestTimeIndex?: number } {
    const slot: { type: "timeSlot"; value: number; bestTimeIndex?: number } = {
      type: "timeSlot",
      value: value,
    };

    const correspondingBestTimeSlot = findBestTimeSlot(day, value);
    if (correspondingBestTimeSlot) {
      slot.bestTimeIndex = correspondingBestTimeSlot.index;
    }

    return slot;
  }

  function createTimeSuggestion(bestTimeSlot: BestTimeSlotApiWithIndex): {
    type: "bestTimeSuggestions";
    value: number;
    online: number;
    bestTimeIndex: number;
  } {
    return {
      type: "bestTimeSuggestions",
      value: bestTimeSlot.offset,
      online: bestTimeSlot.online,
      bestTimeIndex: bestTimeSlot.index,
    };
  }

  return weekDays.map((item: string, day: number) => {
    const schedule = (schedules[day] ?? []).map((value) =>
      createTimeSlot(day, value)
    );

    const unusedBestTimeSlots = withBestTime
      ? (bestTimeByDays[day] ?? [])
          .filter(
            (bestTime) =>
              !schedule.some((slot) => slot.value === bestTime.offset)
          )
          .map(createTimeSuggestion)
      : [];

    return {
      weekDay: item,
      weekDayNumber: day,
      timeSlots: sortBy(
        [...schedule, ...unusedBestTimeSlots],
        (item) => item.value
      ),
    };
  });
}
